import type { Personnel } from './personnel.types';

export interface Location {
  id: string;
  name: string;
  address: string;
}

export interface Team {
  id: string;
  name: string;
  areas: Location[];
  personnel: Personnel[];
}

export interface TeamFormData {
  name: string;
  areaIds: string[];
  personnelIds: string[];
}

// ============================================
// API Types - Aligned with Backend Schema
// ============================================

// Backend JSON field types
export interface TeamEquipmentItem {
  id: number | string;
  name: string;
}

export interface TeamPersonnelItem {
  id: string;
  name: string;
}

// Geometry data for a drawn parcel (rectangle, polygon, circle)
export interface TeamParcelGeometry {
  id: string;
  type: 'rectangle' | 'circle' | 'polygon';
  coords: [number, number][] | [number, number];
  radius?: number;
}

export interface TeamParcelItem {
  id: number | string;
  address: string;
  parcels?: TeamParcelGeometry[]; // The actual geometry data
}

// Raw API Response Type - matches GET /api/teams and GET /api/teams/:id
// Note: equipment, personnel, parcels are stored as TEXT/JSON strings in backend
export type TeamApiResponse = {
  id: string;
  deleted: number;
  user_id: string;
  updated_at: string;
  created_at: string;
  group_id: number;
  incident_id: number;
  team_name: string;
  equipment: string | null; // JSON string from backend
  personnel: string | null; // JSON string from backend
  no_of_personnel: number | null;
  parcels: string | null; // JSON string from backend
  active: number;
};

// Transformed Response Type - for UI consumption (parcels parsed to array)
export type TeamResponse = {
  id: string;
  deleted: number;
  user_id: string;
  updated_at: string;
  created_at: string;
  group_id: number;
  incident_id: number;
  team_name: string;
  equipment: TeamEquipmentItem[] | null;
  personnel: TeamPersonnelItem[] | null;
  no_of_personnel: number | null;
  parcels: TeamParcelItem[] | null; // Parsed for UI
  active: number;
};

// Extended response for GET /api/teams/:id (includes incident dropdown)
export interface TeamDetailResponse extends TeamResponse {
  incident_name?: string;
  incident_dropdown_values?: Array<{
    pvDataID: number;
    pvIncident: string;
  }>;
}

// API Request Type - for POST /api/teams and PUT /api/teams/:id
// Note: equipment, personnel, parcels are sent as JSON strings to backend
export interface TeamPayload {
  id?: string; // Only for PUT (update), omit for POST (create)
  team_name: string;
  group_id: number;
  incident_id: number;
  user_id: string;
  equipment: string | null; // Must be JSON.stringify'd
  personnel: string | null; // Must be JSON.stringify'd
  no_of_personnel: number;
  parcels: string | null; // Must be JSON.stringify'd
  active: number;
}

// UI-friendly payload type (arrays) - transformed to TeamPayload before API call
export interface TeamFormPayload {
  id?: string;
  team_name: string;
  group_id: number;
  incident_id: number;
  user_id: string;
  equipment: TeamEquipmentItem[] | null;
  personnel: TeamPersonnelItem[] | null;
  no_of_personnel: number;
  parcels: TeamParcelItem[] | null;
  active: number;
  deleted: boolean;
}

// Query params for GET /api/teams
export interface TeamListQuery {
  limit?: number;
  offset?: number;
  search?: string;
  filters?: string;
  incidentId?: string;
  groupId?: number;
}

// ============================================
// Legacy types for UI components (map drawing)
// These are used internally and mapped to/from API types
// ============================================

export type TeamAreaResponse = {
  id: string;
  name: string;
  geometry: GeoJSON.Geometry | null;
};

export interface TeamAreaPayload {
  id?: string;
  name: string;
  geometry: GeoJSON.Geometry | null;
}

// ============================================
// Parcels Transformation Helpers
// Backend stores parcels as TEXT, frontend uses TeamParcelItem[]
// ============================================

/**
 * Parse parcels string from backend to TeamParcelItem array
 * Handles both JSON string and comma-separated formats
 */
export function parseParcels(parcelsStr: string | null): TeamParcelItem[] | null {
  if (!parcelsStr) return null;

  // Try parsing as JSON first
  try {
    const parsed = JSON.parse(parcelsStr);
    if (Array.isArray(parsed)) {
      return parsed as TeamParcelItem[];
    }
  } catch {
    // Not JSON, try comma-separated format
  }

  // Handle comma-separated format: "parcel-123, parcel-456"
  const items = parcelsStr
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  if (items.length === 0) return null;

  return items.map((item, index) => ({
    id: index,
    address: item,
  }));
}

/**
 * Stringify TeamParcelItem array to JSON string for backend storage
 */
export function stringifyParcels(parcels: TeamParcelItem[] | null): string | null {
  if (!parcels || parcels.length === 0) return null;
  return JSON.stringify(parcels);
}

/**
 * Parse equipment JSON string from backend to TeamEquipmentItem array
 */
export function parseEquipment(equipmentStr: string | null): TeamEquipmentItem[] | null {
  if (!equipmentStr) return null;
  try {
    const parsed = JSON.parse(equipmentStr);
    if (Array.isArray(parsed)) {
      return parsed as TeamEquipmentItem[];
    }
  } catch {
    // Invalid JSON
  }
  return null;
}

/**
 * Stringify TeamEquipmentItem array to JSON string for backend storage
 */
export function stringifyEquipment(equipment: TeamEquipmentItem[] | null): string | null {
  if (!equipment || equipment.length === 0) return null;
  return JSON.stringify(equipment);
}

/**
 * Parse personnel JSON string from backend to TeamPersonnelItem array
 */
export function parsePersonnel(personnelStr: string | null): TeamPersonnelItem[] | null {
  if (!personnelStr) return null;
  try {
    const parsed = JSON.parse(personnelStr);
    if (Array.isArray(parsed)) {
      return parsed as TeamPersonnelItem[];
    }
  } catch {
    // Invalid JSON
  }
  return null;
}

/**
 * Stringify TeamPersonnelItem array to JSON string for backend storage
 */
export function stringifyPersonnel(personnel: TeamPersonnelItem[] | null): string | null {
  if (!personnel || personnel.length === 0) return null;
  return JSON.stringify(personnel);
}

/**
 * Transform raw API response to UI-friendly format (parse all JSON fields)
 */
export function transformTeamResponse(raw: TeamApiResponse): TeamResponse {
  return {
    ...raw,
    equipment: parseEquipment(raw.equipment),
    personnel: parsePersonnel(raw.personnel),
    parcels: parseParcels(raw.parcels),
  };
}

/**
 * Transform UI form payload to API payload (stringify all JSON fields)
 */
export function transformTeamPayload(form: TeamFormPayload): TeamPayload {
  return {
    ...form,
    equipment: stringifyEquipment(form.equipment),
    personnel: stringifyPersonnel(form.personnel),
    parcels: stringifyParcels(form.parcels),
  };
}
